import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiInfoContextValueHook } from "../../../../../contexts/ApiInfoContext";
import { SellertoolSignatureUtils } from "../../../../../utils/sellertool-signature-utils";
import { PurchaseInfosApi } from "../../../../../apis/PurchaseInfosApi";
import { useEffect } from "react";
import { CustomBackdropController } from "../../../../../components/backdrop/default/v1";
import { customToast } from "../../../../../components/toast/custom-react-toastify/v1";

const QUERY_KEY = 'purchaseInfos';

export function usePurchaseInfosQuery() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const queryResult = useQuery({
        queryKey: [QUERY_KEY],
        enabled: !!(apiInfoContextValueHook?.apiInfo?.apiKey && apiInfoContextValueHook?.apiInfo?.secretKey),
        queryFn: async () => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }
            const fetchResult = await PurchaseInfosApi().searchList({
                headers: headers
            })

            return fetchResult;
        }
    });

    useEffect(() => {
        if (queryResult.isLoading) {
            CustomBackdropController().showBackdrop();
        } else {
            CustomBackdropController().hideBackdrop();
        }
    }, [queryResult.isLoading]);

    return queryResult;
}

export function useCreatePurchaseInfoMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await PurchaseInfosApi().createOne({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('구매정보가 추가 되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useUpdatePurchaseInfosMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await PurchaseInfosApi().updateList({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('구매정보가 업데이트 되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useDeletePurchaseInfoMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ uuid }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await PurchaseInfosApi().deleteOne({
                headers: headers,
                params: {
                    uuid: uuid
                }
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('구매정보가 삭제 되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}